/**
 * React Query hooks for the superadmin cache-telemetry dashboard.
 *
 * @module pages/superadmin/cache-telemetry/useCacheTelemetryData
 */
import { useQuery } from '@tanstack/react-query';
import { API_URL } from '../../../../config';
import type {
  CacheTelemetryMetric,
  CompileValidityTrendPoint,
  NodeMissTrendPoint,
  ExecTierTrendPoint,
  StepStrategyTrendPoint,
  ActionTierTrendPoint,
  CompileFailureRow,
  NodeMissRow,
  StepStrategyFailureRow,
} from '@chatsdet/types';

type MetricToTrend = {
  compile_validity: CompileValidityTrendPoint;
  node_miss: NodeMissTrendPoint;
  exec_tier: ExecTierTrendPoint;
  step_strategy: StepStrategyTrendPoint;
  action_tier: ActionTierTrendPoint;
};

interface TrendsResponse<T> {
  ok: boolean;
  metric: CacheTelemetryMetric;
  days: number;
  trends: T[];
}

const buildQuery = (params: Record<string, string | number | undefined>) => {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') sp.set(k, String(v));
  }
  return sp.toString();
};

/** Trend series for a single metric family, day-bucketed. */
export function useCacheTelemetryTrends<M extends CacheTelemetryMetric>(
  metric: M,
  opts: { days: number; workflowId?: string }
) {
  return useQuery({
    queryKey: ['cache-telemetry-trends', metric, opts.days, opts.workflowId],
    queryFn: async (): Promise<MetricToTrend[M][]> => {
      const qs = buildQuery({ metric, days: opts.days, workflowId: opts.workflowId });
      const res = await fetch(`${API_URL}/superadmin/cache-telemetry/trends?${qs}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Failed to load ${metric} trends (${res.status})`);
      const body = (await res.json()) as TrendsResponse<MetricToTrend[M]>;
      return body.trends ?? [];
    },
  });
}

type DrillDownKind = 'compile-failures' | 'node-misses' | 'step-strategy-failures';
type DrillRow<K extends DrillDownKind> = K extends 'compile-failures'
  ? CompileFailureRow
  : K extends 'node-misses'
    ? NodeMissRow
    : StepStrategyFailureRow;

/** Recent failing nodes/steps for a drill-down panel. `enabled` gates the fetch. */
export function useCacheTelemetryDrillDown<K extends DrillDownKind>(
  kind: K,
  opts: { days: number; workflowId?: string; enabled: boolean }
) {
  return useQuery({
    queryKey: ['cache-telemetry-drilldown', kind, opts.days, opts.workflowId],
    enabled: opts.enabled,
    queryFn: async (): Promise<DrillRow<K>[]> => {
      const qs = buildQuery({ days: opts.days, workflowId: opts.workflowId });
      const res = await fetch(`${API_URL}/superadmin/cache-telemetry/${kind}?${qs}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`Failed to load ${kind} (${res.status})`);
      const body = (await res.json()) as { ok: boolean; rows: DrillRow<K>[] };
      return body.rows ?? [];
    },
  });
}
